import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import appSettings from 'wanderu-config';

import ElementQuery from '../ElementQuery/ElementQuery';
import PageSection from '../PageSection/PageSection';
import SectionTitle from '../SectionTitle/SectionTitle';
import Accordion from '../WDS/Accordion/Accordion';

import styles from './ContentSection.scss';

function Content(props) {
    const {
        children,
        className = '',
    } = props;

    return (
        <div className={`${styles.content} ${className}`}>
            {children}
        </div>
    );
}

function Sidebar(props) {
    const {
        children,
        className = '',
    } = props;

    return (
        <aside
            className={`${styles.sidebar} ${className}`}
            role="note"
        >
            {children}
        </aside>
    );
}

const TitleWithChildren = (props) => {
    const {
        headerClass,
        children,
        id,
        title,
    } = props;

    return (
        <>
            <SectionTitle
                className={`${headerClass} ${styles.title}`}
                id={id}
            >
                {title}
            </SectionTitle>
            {children}
        </>
    );
};

const CollapsableComponent = (props) => {
    const {
        isLargeScreen: _isLargeScreen,
        isMediumScreen: _isMediumScreen,
        isSmallScreen,
        layoutClassName: _layoutClassName,
        containerClass,
        useAccordionComponent,
        isOpen: defaultIsOpen,
        ...otherProps
    } = props;

    const isOpen = defaultIsOpen !== undefined
        ? defaultIsOpen
        : !isSmallScreen || !useAccordionComponent;

    return (
        isSmallScreen || useAccordionComponent
            ? (
                <Accordion
                    {...otherProps}
                    containerClass={containerClass}
                    contentClass={`${styles.contentSection} ${styles.accordionContent}`}
                    headerClass={styles.accordionHeader}
                    isOpen={isOpen}
                    showChevron={useAccordionComponent}
                />
            )
            : <TitleWithChildren {...otherProps} />
    );
};

function ResponsivePanel(props) {
    const element = appSettings.env.isClient
        ? document.body
        : null;

    return (
        <ElementQuery element={element}>
            <CollapsableComponent {...props} />
        </ElementQuery>
    );
}

const ContentSection = (props) => {
    const {
        useAccordionComponent,
        layoutClassName,
        className,
        id,
    } = props;

    return (
        <PageSection
            aria-labelledby={id}
            className={className}
            layoutClassName={classNames(
                styles.contentSection,
                { [styles.accordionLayout]: useAccordionComponent },
                layoutClassName,
            )}
        >
            {props.isCollapsable
                ? <ResponsivePanel {...props} />
                : <TitleWithChildren {...props} />}
        </PageSection>
    );
};

ContentSection.propTypes = {
    containerClass: PropTypes.string,
    layoutClassName: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.string.isRequired,
    isCollapsable: PropTypes.bool,
    title: PropTypes.string,
    useAccordionComponent: PropTypes.bool,
    defaultOpen: PropTypes.bool,
};

ContentSection.defaultProps = {
    containerClass: '',
    layoutClassName: '',
    className: '',
    title: '',
    isCollapsable: false,
    useAccordionComponent: false,
    defaultOpen: false,
};

export {
    CollapsableComponent,
    Content,
    ContentSection,
    ResponsivePanel,
    Sidebar,
};

export default ContentSection;
